// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — in-bridge documentEpoch belief cache (ABB-S6.4, 1A)
// ═══════════════════════════════════════════════════════════════════════════
//
// The MINIMAL in-bridge cache of the CURRENT documentEpoch / sessionEpoch belief. It
// closes the project-OUT ⇔ hydrate-IN symmetry the client's `refreshDocumentIdentityIfStale`
// flagged as the S6 gate-marker (agentBridgeClient.ts:614-622):
//   • project-OUT (S3/S5): a LOCAL document swap → `document-changed` to the companion;
//   • hydrate-IN (THIS, S6): a NEW companion-side documentEpoch surfaced in a welcome /
//     session-changed adoption → cached here → consumed by `stateReadHandler` so a read
//     routes to the CORRECT snapshot belief (not a stale pre-swap one).
//
// WHY a cache (1A, user-confirmed): the `state.read` handler must bind every read to the
// document the COMPANION currently believes is active — if a remote/late swap re-keyed the
// document AFTER the snapshot the handler is about to project, the read must reflect the
// NEW belief, not the stale one. The cache is the single in-bridge source of that belief;
// the handler reads `cache.get()` and the swap-adoption path writes `cache.hydrate(...)`.
//
// INV-OFF (ПРИОРИТЕТ #1): a plain factory — no auto-subscribe, no editorStore, no socket.
// S7 wires `onDocumentChanged` (the hydrate callback) into the client's welcome /
// session-changed adoption so a real swap updates the cache; until then the cache is INERT
// (created on demand, never auto-instantiated).
//
// LEAF module (ПРИОРИТЕТ #2): imports NOTHING (no editorStore, no codec, no socket). A
// pure in-memory belief holder. `npm run deps:graph` cycles=0.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * The in-bridge document/session belief — the companion's authoritative quad members the
 * read path binds to. `documentEpoch` is the monotonic per-document swap counter (ABB-S1);
 * `sessionEpoch` is the ownership epoch the welcome assigned. Both `null` before pairing.
 */
export interface DocumentEpochBelief {
  readonly documentId: string | null;
  readonly documentEpoch: number | null;
  readonly sessionEpoch: number | null;
}

/** The null belief (before the first welcome / after a reset). */
const NULL_BELIEF: DocumentEpochBelief = {
  documentId: null,
  documentEpoch: null,
  sessionEpoch: null,
};

/** A listener notified whenever the cached belief CHANGES (a real swap / re-key). */
export type DocumentEpochListener = (belief: DocumentEpochBelief) => void;

/** The 1A cache surface (the read handler reads `get`; the adoption path calls `hydrate`). */
export interface DocumentEpochCache {
  /** The current cached belief (the read handler binds each read to this). */
  get(): DocumentEpochBelief;
  /**
   * HYDRATE-IN: surface a NEW companion-side belief (a welcome / session-changed adoption,
   * S3). Idempotent for an UNCHANGED belief (no listener fire); a CHANGED belief replaces +
   * notifies listeners. This is the DI `onDocumentChanged` callback S7 wires into the
   * client's adoption path — closing the project-OUT ⇔ hydrate-IN symmetry.
   */
  hydrate(belief: DocumentEpochBelief): void;
  /** Reset to the null belief (a disconnect / hard session death). Notifies on a change. */
  reset(): void;
  /** Subscribe to belief CHANGES (the read seam / future consumers). Returns an unsubscribe. */
  subscribe(listener: DocumentEpochListener): () => void;
}

/** Whether two beliefs are member-equal (an unchanged hydrate is a no-op — no listener fire). */
function beliefsEqual(a: DocumentEpochBelief, b: DocumentEpochBelief): boolean {
  return (
    a.documentId === b.documentId &&
    a.documentEpoch === b.documentEpoch &&
    a.sessionEpoch === b.sessionEpoch
  );
}

/**
 * Create the in-bridge documentEpoch belief cache (ABB-S6.4, 1A). INERT on construction
 * (the null belief, no listeners, no subscription). S7 calls `hydrate` from the client's
 * welcome / session-changed adoption + `reset` on disconnect; the read handler reads `get`.
 */
export function createDocumentEpochCache(
  initial: DocumentEpochBelief = NULL_BELIEF,
): DocumentEpochCache {
  let belief: DocumentEpochBelief = initial;
  const listeners = new Set<DocumentEpochListener>();

  const notify = (): void => {
    for (const listener of listeners) {
      listener(belief);
    }
  };

  return {
    get() {
      return belief;
    },
    hydrate(next) {
      // Normalize to the canonical member set (defensive — a caller may pass extra keys).
      const normalized: DocumentEpochBelief = {
        documentId: next.documentId,
        documentEpoch: next.documentEpoch,
        sessionEpoch: next.sessionEpoch,
      };
      if (beliefsEqual(belief, normalized)) {
        return; // unchanged → no replace, no listener fire (idempotent adoption).
      }
      belief = normalized;
      notify();
    },
    reset() {
      if (beliefsEqual(belief, NULL_BELIEF)) {
        return;
      }
      belief = NULL_BELIEF;
      notify();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
